import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import moment from 'moment';
import { fetchOrganizationIfNeeded } from '@pec/aion-ui-core/actions/organization';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import { formats } from '@pec/aion-ui-i18next/constants';
import { useFeature } from 'flagged';

import { UpgradeTile } from '../../containers/Upgrade/UpgradeTile';
import { useTypedSelector, useAppDispatch } from '../../combineReducers';
import { FOUNDATION_EXPIRATION_DATE, LOGOS } from '../../helpers/newDashboard';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: `${theme.spacing(2)}px`,
      margin: theme.spacing(2)
    },
    subscriptionContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      gap: `${theme.spacing(1)}px`
    },
    logo: {
      height: 65,
      width: 65
    },
    greeting: {
      fontSize: 24
    },
    subscriptionData: {
      display: 'flex',
      flexDirection: 'row',
      gap: `${theme.spacing(0.5)}px`,
      fontSize: 12
    },
    upgradeButtonContainer: {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'end'
    }
  })
);

type Props = {
  organizationId: string;
};

const greeting = (t: TFunction) => {
  const today = new Date();
  const hour = today.getHours();

  if (hour < 12) {
    return t('newDashboard.subscriptionDetails.goodMorning', 'Good morning');
  } else if (hour < 18) {
    return t('newDashboard.subscriptionDetails.goodAfternoon', 'Good afternoon');
  } else {
    return t('newDashboard.subscriptionDetails.goodEvening', 'Good evening');
  }
};

export const DashboardSubscriptionDetails: React.FC<Props> = ({ organizationId }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('subscriber');
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);
  const { userInfo } = useTypedSelector(state => state.userInfo);
  const { organization } = useTypedSelector(state => state.organization);
  const membershipNumber =
    organization?.organizationRegistrationNumbers?.find(regNumber => regNumber.typeId === 5)?.value || null;

  const recurringBillingMessageFeatureEnabled = useFeature('RecurringBillingAutoRenewalStatusMessageEnabled');

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchOrganizationIfNeeded(organizationId));
    }
  }, [organizationId, dispatch]);

  if (!mainSubscription) {
    return null;
  }

  const {
    countryISO2,
    description,
    expirationDate,
    typePublicId,
    previousExpirationDate,
    recurringBillingEnabled
  } = mainSubscription;
  const Logo = countryISO2 === 'US' ? LOGOS['US'] : LOGOS[typePublicId];
  const isExpired = expirationDate ? moment(expirationDate).isBefore() : false;
  const getExpirationDateString = (expirationDate: string | null) =>
    expirationDate
      ? localizeDate(
          {
            value: `${moment.utc(expirationDate).toISOString()}`,
            config: { day: '2-digit', month: '2-digit', year: 'numeric' }
          },
          t,
          formats.dateCustom
        )
      : '';
  const isFoundation = description === SubscriptionTiers.Foundation;
  const isRecurringBillingMessageEnabled = recurringBillingMessageFeatureEnabled && recurringBillingEnabled;

  return (
    <Box className={classes.container}>
      <Box className={classes.subscriptionContainer}>
        {Logo ? <Logo className={classes.logo} /> : null}
        <Box>
          <Box className={classes.greeting}>
            {greeting(t)}
            {userInfo?.firstName ? `, ${userInfo.firstName}` : ''}
          </Box>
          <Box className={classes.subscriptionData}>
            <Box>{description}</Box>
            {membershipNumber ? (
              <>
                <Box>|</Box>
                <Box>{membershipNumber}</Box>
              </>
            ) : null}
            {expirationDate && moment(expirationDate).isBefore(FOUNDATION_EXPIRATION_DATE) && (
              <>
                <Box>|</Box>
                <Box>
                  {isRecurringBillingMessageEnabled
                    ? t('newDashboard.subscriptionDetails.autoRenew', 'Membership is set to auto-renew')
                    : isExpired
                    ? t('newDashboard.subscriptionDetails.expired', 'Membership Expired')
                    : t('newDashboard.subscriptionDetails.expires', 'Membership Expires')}{' '}
                  {getExpirationDateString(expirationDate)}
                </Box>
              </>
            )}
            {isFoundation && previousExpirationDate && (
              <>
                <Box>|</Box>
                <Box>
                  {t('newDashboard.subscriptionDetails.expired', 'Membership Expired')}{' '}
                  {getExpirationDateString(previousExpirationDate)}
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Box className={classes.upgradeButtonContainer}>
          <UpgradeTile />
        </Box>
      </Box>
    </Box>
  );
};
